import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import { toast } from 'react-toastify'
import { Breadcrumb, Loader, Table } from '../../components'
import apiService from '../../features/api/service'
import { fetchEvent } from '../../features/event/slice'
import { useInternationalization } from '../../features/i18n'
import ContainerOnMobile from '../../layouts/container-on-mobile'
import { errorHandler } from '../../utils'
// import data from './data'

const DashboardGuestList = ({ match }) => {
  const dispatch = useDispatch()
  const status = useSelector(state => state.event.status)
  const {
    id: eventId,
    guest_total,
    guest_seat,
    guest_data = []
  } = useSelector(state => state.eventForm.data)
  const [requesting, setRequesting] = useState(false)
  const { translate } = useInternationalization()

  useEffect(() => {
    if(!eventId && status === 'idle' && match.params && match.params.id){
      dispatch(fetchEvent(match.params.id))
    }
  }, [dispatch, match.params, status, eventId])

  const routes = [
    {
      name: translate('NAVIGATION', 'dashboard'),
      route: '/dashboard'
    },
    {
      name: translate('EVENT', 'our'),
      route: '/dashboard/events'
    },
    {
      name: translate('EVENT', 'details'),
      route: `/dashboard/events/update/${eventId}`
    }
  ]

  const handleDelete = useCallback((id) => {
    if(id.includes('external')){
      // Delete external participants

      const parentData = guest_data.find(d => d.id === id)
      if(parentData){
        const guestsToDelete = parentData.ids
        if(guestsToDelete.length > 0){
          const promises = []
          for (let index = 0; index < guestsToDelete.length; index++) {
            const pkId = guestsToDelete[index];
            promises.push(apiService.delete('participant_external', pkId))
          }

          setRequesting(true)

          Promise.all(promises)
            .then(() => {
              toast.success('Deleted.')
              dispatch(fetchEvent(eventId))
            })
            .catch(err => {
              const msg = errorHandler(err)
              toast.error(msg)
            })
            .finally(() => setRequesting(false))
        } else {
          toast.error('Error 400: empty data.')
        }
      } else {
        toast.error('Error 400: no data found.')
      }
    } else {
      setRequesting(true)

      // Delete internal participant
      apiService.delete('participant_internal', id)
        .then(() => {
          toast.success('Deleted.')
          dispatch(fetchEvent(eventId))
        })
        .catch(err => {
          const msg = errorHandler(err)
          toast.error(msg)
        })
        .finally(() => setRequesting(false))
    }
  }, [dispatch, eventId, guest_data])

  const tableColumns = useMemo(
    () => [
      {
        Header: 'Order #',
        accessor: 'order',
      },
      {
        Header: translate('EVENT', 'registrant'),
        accessor: 'name',
      },
      {
        Header: translate('GUEST', 'plural'),
        accessor: 'guests',
      },
      {
        Header: '',
        accessor: 'id',
        Cell: ({value}) => (
          <div className="flex flex-wrap mt-2 lg:mt-0">
            <p className="cursor-pointer underline text-sm text-gray-999 mr-2">
              <Link to={`/dashboard/events/${eventId}/guest-list/${value}`}>
                {translate('GENERAL', 'view')}
              </Link>
            </p>
            <p
              className="cursor-pointer underline text-sm text-gray-999"
              onClick={() => handleDelete(value)}
            >
              {translate('GENERAL', 'delete')}
            </p>
          </div>
        )
      }
    ],
    [handleDelete, eventId, translate]
  )

  return <ContainerOnMobile>
    <div className="hidden lg:block">
      <Breadcrumb routes={routes}/>
    </div>
    <div className="flex items-end mt-8 mb-5 lg:mt-2 lg:mb-2">
      <h1 className="text-lg lg:text-4xl font-primary font-bold mr-2 leading-tight lg:leading-none">
        {translate('EVENT', 'guests')}
      </h1>
      <p className="font-bold text-xs lg:text-lg">({guest_total}/{guest_seat})</p>
    </div>
    <hr className="hidden lg:block mt-5 mb-8"/>
    <div className="mobile-fullwidth overflow-x-auto">
      {
        requesting &&
        <div className="flex items-center justify-center">
          <Loader />
        </div>
      }
      <Table
        data={guest_data}
        columns={tableColumns}
      />
    </div>
  </ContainerOnMobile>
}

export { DashboardGuestList }
